import * as Sentry from '@sentry/react';

import {
  classifyError,
  type AxiosLikeError,
  type ClassifiedError,
  type ErrorKind,
} from '../errors/classifyError';

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
// O valor DECLARADO decide se reporta; o efetivo (com fallback para o MODE do
// Vite) só rotula o evento. Sem essa separação, um build feito sem `.env` cairia
// em MODE='production' e reportaria como se fosse deploy.
const ENVIRONMENT_DECLARADO = import.meta.env.VITE_SENTRY_ENVIRONMENT as
  | string
  | undefined;
const ENVIRONMENT = ENVIRONMENT_DECLARADO || import.meta.env.MODE;
const RELEASE = import.meta.env.VITE_SENTRY_RELEASE as string | undefined;
// Em dev local o Sentry fica DESLIGADO por padrão: o .env de dev compartilha o
// mesmo DSN do projeto de STG, então erros locais poluiriam o monitoramento.
// Setar VITE_SENTRY_DEV_ENABLED=true para reativar quando o objetivo for
// justamente testar o envio de eventos ao Sentry localmente.
const DEV_SENTRY_ENABLED = import.meta.env.VITE_SENTRY_DEV_ENABLED === 'true';
// Ambientes cujos erros pertencem ao monitoramento. Qualquer outro valor (`local`,
// `development`, `test`) é máquina de alguém.
const ENVIRONMENTS_REPORTAVEIS = new Set(['staging', 'production']);

const SENSITIVE_HEADERS = new Set(['authorization', 'cookie', 'x-api-key']);

function scrubHeaders(headers: Record<string, string> | undefined): void {
  if (!headers) return;
  for (const key of Object.keys(headers)) {
    if (SENSITIVE_HEADERS.has(key.toLowerCase())) {
      headers[key] = '[Filtered]';
    }
  }
}

// Segmentos dinâmicos de URL são substituídos por placeholders para que o
// fingerprint do Sentry agrupe por operação, e não por recurso individual.
// Sem isso, cada processo / UUID / identificador produziria uma issue própria.
const CNJ_RE = /^\d{7}-\d{2}\.\d{4}\.\d\.\d{2}\.\d{4}$/;
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_ID_RE = /^\d+$/;

/**
 * Normaliza o path de uma request substituindo segmentos dinâmicos por
 * placeholders estáveis. Mantida pura e isolada para ser testável.
 *
 * Regras avaliadas por segmento, nesta ordem: CNJ vira o placeholder de
 * processo, UUID vira ":uuid" e identificador puramente numérico vira ":id".
 * A query string é descartada e o retorno é "unknown" quando a url está
 * ausente.
 */
export function normalizePath(url: string | undefined): string {
  if (!url) return 'unknown';

  // Descarta query string e fragmento.
  const pathOnly = url.split('?')[0].split('#')[0];

  const normalized = pathOnly
    .split('/')
    .map((segment) => {
      if (!segment) return segment;
      if (CNJ_RE.test(segment)) return ':numero';
      if (UUID_RE.test(segment)) return ':uuid';
      if (NUMERIC_ID_RE.test(segment)) return ':id';
      return segment;
    })
    .join('/');

  return normalized || 'unknown';
}

export function initSentry(): void {
  if (!DSN) {
    // Dev local sem chave configurada — silenciar para não poluir o console.
    return;
  }

  // Build de dev (vite) não envia por padrão — só com opt-in explícito.
  // Builds de STG/PROD (vite build) têm DEV=false e seguem reportando normalmente.
  if (import.meta.env.DEV && !DEV_SENTRY_ENABLED) {
    return;
  }

  // O guard acima cobre `vite dev`, não `vite build`: um bundle servido na rede
  // local (harness e2e no devhost, preview de produção na máquina) tem DEV=false e
  // pega o DSN de STG do `.env` do repo — foi assim que a navegação no e2e virou
  // issue no Sentry e e-mail de alerta (incidente 2026-08-15). Reportar exige
  // ambiente monitorado DECLARADO: os workflows de deploy setam a variável
  // explicitamente (`production`/`staging`), e nada mais no repo o faz — o `.env`
  // diz `local`. Deliberadamente não se exige `RELEASE` aqui: um deploy que
  // esquecesse de injetá-la deixaria o Sentry MUDO em produção, falha bem pior
  // que o ruído local. VITE_SENTRY_DEV_ENABLED=true reativa para teste local.
  if (!DEV_SENTRY_ENABLED && !ENVIRONMENTS_REPORTAVEIS.has(ENVIRONMENT_DECLARADO ?? '')) {
    return;
  }

  Sentry.init({
    dsn: DSN,
    environment: ENVIRONMENT,
    release: RELEASE,
    sendDefaultPii: false,
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    integrations: [Sentry.browserTracingIntegration()],
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
    ],
    beforeSend(event) {
      scrubHeaders(event.request?.headers);
      if (event.request?.data) {
        event.request.data = '[Filtered]';
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
        const data = breadcrumb.data as Record<string, unknown> | undefined;
        if (data?.['http.request.headers.authorization']) {
          data['http.request.headers.authorization'] = '[Filtered]';
        }
      }
      return breadcrumb;
    },
  });
}

export function setSentryUser(userId: string): void {
  Sentry.setUser({ id: userId });
}

export function clearSentryUser(): void {
  Sentry.setUser(null);
}

export function setSentryRequestId(requestId: string | null): void {
  if (requestId) {
    Sentry.setTag('request_id', requestId);
  }
}

/**
 * Loga uma transição de diagnóstico — em DEV vai pro console (visível direto),
 * em PROD vira breadcrumb Sentry (anexado ao próximo evento capturado).
 *
 * Útil para rastrear máquinas de estado complexas (ex.: ciclo de vida de
 * `useDocumentoBinario`: entrou-em-preparing → safety-timeout → ready) sem
 * poluir o console do usuário em produção mas mantendo o rastro disponível
 * quando algo realmente quebra.
 */
export function traceDiagnostic(
  category: string,
  message: string,
  data?: Record<string, unknown>,
): void {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.info(`[${category}] ${message}`, data);
  }
  Sentry.addBreadcrumb({
    category,
    level: 'info',
    message,
    data,
  });
}

export function captureReactBoundaryError(error: Error, componentStack?: string): void {
  Sentry.captureException(error, {
    contexts: componentStack ? { react: { componentStack } } : undefined,
  });
}

type SentryAction = 'breadcrumb' | 'capture';
type SentryLevel = 'info' | 'warning' | 'error';

interface ErrorPolicy {
  action: SentryAction;
  level: SentryLevel;
}

// Política declarativa: cada ErrorKind do classifyError mapeia para uma ação
// no Sentry. Bug ou anomalia técnica → captureException; estado esperado de
// fluxo → addBreadcrumb (vira contexto para o próximo erro real, sem inundar
// o painel com 401/422/403 esperados).
const SENTRY_POLICY: Record<ErrorKind, ErrorPolicy> = {
  auth:                 { action: 'breadcrumb', level: 'info' },
  forbidden:            { action: 'breadcrumb', level: 'warning' },
  payment_required:     { action: 'breadcrumb', level: 'info' },
  not_found:            { action: 'breadcrumb', level: 'warning' },
  conflict:             { action: 'capture',    level: 'warning' },
  validation:           { action: 'breadcrumb', level: 'info' },
  certificate_required: { action: 'breadcrumb', level: 'info' },
  pdpj_required:        { action: 'breadcrumb', level: 'info' },
  rate_limited:         { action: 'capture',    level: 'warning' },
  not_implemented:      { action: 'capture',    level: 'warning' },
  service_unavailable:  { action: 'capture',    level: 'error' },
  server:               { action: 'capture',    level: 'error' },
  network:              { action: 'capture',    level: 'error' },
  unknown:              { action: 'capture',    level: 'error' },
};

/**
 * Tags e contexto extras opcionais para enriquecer o evento Sentry mantendo
 * a política central (`SENTRY_POLICY`) como autoridade sobre capture vs breadcrumb.
 *
 * Use para callers que têm contexto específico de domínio (ex.: download de
 * documento processual carrega `document_uuid`, `mime_type`, `viewer_kind`) sem
 * abrir o caminho paralelo `Sentry.captureException` direto.
 */
export interface ErrorEnrichment {
  /** Tags adicionais (snake_case por convenção do projeto: `document_uuid`, `processo_numero`...). */
  tags?: Record<string, string>;
  /** Contexto nomeado adicional — aparece como bloco separado no evento Sentry. */
  context?: { name: string; data: Record<string, unknown> };
}

export function reportClassifiedError(
  error: unknown,
  classified?: ClassifiedError,
  enrich?: ErrorEnrichment,
): void {
  const c = classified ?? classifyError(error);
  const policy = SENTRY_POLICY[c.kind];

  const data: Record<string, unknown> = {
    kind: c.kind,
    status: c.status ?? null,
    detail: c.detail,
  };

  if (policy.action === 'breadcrumb') {
    Sentry.addBreadcrumb({
      category: 'error.handled',
      level: policy.level,
      message: c.message,
      data: enrich?.tags ? { ...data, ...enrich.tags } : data,
    });
    return;
  }

  const tags: Record<string, string> = {
    error_kind: c.kind,
    http_status: c.status !== undefined ? String(c.status) : 'none',
    ...(enrich?.tags ?? {}),
  };
  // network: separa "offline real" de "backend caiu" — chave de filtro
  // óbvia no painel para investigar bugs em PWA com cache agressivo.
  if (c.kind === 'network') {
    tags.offline = String(!navigator.onLine);
  }

  const contexts: Record<string, Record<string, unknown>> = {
    classified: data as Record<string, unknown>,
  };
  if (enrich?.context) {
    contexts[enrich.context.name] = enrich.context.data;
  }

  Sentry.captureException(error, {
    level: policy.level,
    tags,
    contexts,
    fingerprint: buildFingerprint(error, c.kind),
  });
}

/**
 * Deriva centralmente um fingerprint por OPERAÇÃO a partir do próprio
 * AxiosError, sem exigir que cada call-site passe um rótulo.
 *
 * Para erro axios: ["api", METHOD, pathNormalizado, kind]. Assim cada operação
 * (ex.: upload de certificado) vira uma issue própria, e 500 vs 502 da MESMA
 * operação agrupam juntos — por isso usamos `kind` (classe) e nunca o status
 * cru. Para erro não-axios (sem `config`): ["app", kind], um agrupamento são
 * por classe de erro em vez do frame compartilhado do axios.
 */
function buildFingerprint(error: unknown, kind: ErrorKind): string[] {
  const axiosError = error as AxiosLikeError;
  const config = axiosError?.config;

  if (!config) {
    return ['app', kind];
  }

  const method = config.method ? config.method.toUpperCase() : 'UNK';
  const path = normalizePath(config.url);
  return ['api', method, path, kind];
}
